import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Chip,
  Card,
  CardContent,
  CardMedia,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Grid,
  Divider,
  List,
  ListItem,
  ListItemText,
  Avatar,
  ImageList,
  ImageListItem
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  Add as AddIcon,
  PhotoCamera as PhotoCameraIcon,
  Delete as DeleteIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { Service, ServiceDayComment } from '../types';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import { compressImages } from '../utils/imageUtils';
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const DAYS_OF_WEEK = [
  { value: 1, label: 'Lunes', short: 'Lun' },
  { value: 2, label: 'Martes', short: 'Mar' },
  { value: 3, label: 'Miércoles', short: 'Mié' },
  { value: 4, label: 'Jueves', short: 'Jue' },
  { value: 5, label: 'Viernes', short: 'Vie' },
  { value: 6, label: 'Sábado', short: 'Sáb' },
  { value: 0, label: 'Domingo', short: 'Dom' }
];

const ServiceDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  
  const [service, setService] = useState<Service | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedDay, setSelectedDay] = useState<number>(new Date().getDay());
  const [selectedDate, setSelectedDate] = useState<string>(
    new Date().toISOString().split('T')[0]
  );
  const [comments, setComments] = useState<ServiceDayComment[]>([]);
  
  // Estado para agregar comentario
  const [addCommentOpen, setAddCommentOpen] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [previewImages, setPreviewImages] = useState<string[]>([]);
  
  // Estado para ver imagen en fullscreen
  const [fullscreenImage, setFullscreenImage] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      loadService();
    }
  }, [id]);

  useEffect(() => {
    if (id && selectedDate) {
      loadComments();
    }
  }, [id, selectedDate]);

  const loadService = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/services/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error('Error al cargar servicio');
      }

      const data = await response.json();
      setService(data);
      setError(null);
    } catch (err) {
      console.error('Error:', err);
      setError('Error al cargar servicio');
    } finally {
      setLoading(false);
    }
  };

  const loadComments = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/services/${id}/comments?date=${selectedDate}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.ok) {
        const data = await response.json();
        setComments(data);
      }
    } catch (err) {
      console.error('Error al cargar comentarios:', err);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;

    const files = Array.from(e.target.files);
    setSelectedFiles(prev => [...prev, ...files]);

    // Crear previews
    files.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImages(prev => [...prev, reader.result as string]);
      };
      reader.readAsDataURL(file);
    });
  };

  const handleRemoveFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, idx) => idx !== index));
    setPreviewImages(prev => prev.filter((_, idx) => idx !== index));
  };

  const handleAddComment = async () => {
    if (!newComment.trim() && selectedFiles.length === 0) {
      setError('Debes agregar un comentario o al menos una foto');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      
      formData.append('comment', newComment);
      formData.append('dayOfWeek', selectedDay.toString());
      formData.append('date', selectedDate);

      // Comprimir imágenes
      const compressedFiles = await compressImages(selectedFiles);
      compressedFiles.forEach(file => {
        formData.append('photos', file);
      });

      const response = await fetch(`${API_URL}/services/${id}/comments`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });

      if (!response.ok) {
        throw new Error('Error al agregar comentario');
      }

      // Limpiar formulario
      setNewComment('');
      setSelectedFiles([]);
      setPreviewImages([]);
      setAddCommentOpen(false);

      // Recargar comentarios
      await loadComments();
    } catch (err) {
      console.error('Error:', err);
      setError('Error al agregar comentario');
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    if (!window.confirm('¿Estás seguro de eliminar este comentario?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/services/comments/${commentId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error('Error al eliminar comentario');
      }

      await loadComments();
    } catch (err) {
      console.error('Error:', err);
      setError('Error al eliminar comentario');
    }
  };

  const getTasksForDay = (dayOfWeek: number) => {
    if (!service || !service.tasks) return [];
    return service.tasks
      .filter(t => t.dayOfWeek === dayOfWeek)
      .sort((a, b) => a.orderIndex - b.orderIndex);
  };

  const getDayLabel = (dayValue: number) => {
    return DAYS_OF_WEEK.find(d => d.value === dayValue)?.label || '';
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Cargando...</Typography>
      </Box>
    );
  }

  if (!service) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Servicio no encontrado</Alert>
        <Button onClick={() => navigate('/services')} sx={{ mt: 2 }}>
          Volver
        </Button>
      </Box>
    );
  }

  const tasksForSelectedDay = getTasksForDay(selectedDay);

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate('/services')}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4">{service.name}</Typography>
          {!service.isActive && (
            <Chip label="Inactivo" color="default" />
          )}
        </Box>
        <Button
          variant="outlined"
          startIcon={<EditIcon />}
          onClick={() => navigate(`/services/${id}/edit`)}
        >
          Editar
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Información del servicio */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              Información General
            </Typography>
            <Divider sx={{ mb: 2 }} />

            {service.description && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Descripción
                </Typography>
                <Typography variant="body2">{service.description}</Typography>
              </Box>
            )}

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Cliente
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {service.company ? (
                  <>
                    <BusinessIcon color="primary" fontSize="small" />
                    <Typography variant="body2">{service.company.name}</Typography>
                  </>
                ) : service.individual ? (
                  <>
                    <PersonIcon color="secondary" fontSize="small" />
                    <Typography variant="body2">{service.individual.name}</Typography>
                  </>
                ) : (
                  <Typography variant="body2">-</Typography>
                )}
              </Box>
            </Box>

            {service.responsibles && service.responsibles.length > 0 && (
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Responsables
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {service.responsibles.map(resp => (
                    <Chip
                      key={resp.id}
                      label={`${resp.user.firstName} ${resp.user.lastName}`}
                      size="small"
                      variant="outlined"
                    />
                  ))}
                </Box>
              </Box>
            )}
          </Paper>

          {/* Selector de día */}
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Seleccionar Día
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <TextField
              type="date"
              value={selectedDate}
              onChange={(e) => {
                setSelectedDate(e.target.value);
                const date = new Date(e.target.value + 'T00:00:00');
                setSelectedDay(date.getDay());
              }}
              fullWidth
              sx={{ mb: 2 }}
            />

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {DAYS_OF_WEEK.map(day => (
                <Chip
                  key={day.value}
                  label={day.short}
                  onClick={() => setSelectedDay(day.value)}
                  color={selectedDay === day.value ? 'primary' : 'default'}
                  variant={selectedDay === day.value ? 'filled' : 'outlined'}
                />
              ))}
            </Box>
          </Paper>
        </Grid>

        {/* Tareas y comentarios del día seleccionado */}
        <Grid item xs={12} md={8}>
          {/* Tareas del día */}
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              Tareas - {getDayLabel(selectedDay)}
            </Typography>
            <Divider sx={{ mb: 2 }} />

            {tasksForSelectedDay.length > 0 ? (
              <List>
                {tasksForSelectedDay.map((task, idx) => (
                  <ListItem key={task.id}>
                    <ListItemText
                      primary={`${idx + 1}. ${task.taskText}`}
                    />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No hay tareas programadas para este día
              </Typography>
            )}
          </Paper>

          {/* Comentarios */}
          <Paper sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Comentarios y Fotos
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setAddCommentOpen(true)}
                size="small"
              >
                Agregar
              </Button>
            </Box>
            <Divider sx={{ mb: 2 }} />

            {comments.length > 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {comments.map(comment => (
                  <Card key={comment.id} variant="outlined">
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ width: 32, height: 32 }}>
                            {comment.user.firstName[0]}
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2">
                              {comment.user.firstName} {comment.user.lastName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {format(parseISO(comment.createdAt), 'PPp', { locale: es })}
                            </Typography>
                          </Box>
                        </Box>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteComment(comment.id)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>

                      {comment.comment && (
                        <Typography variant="body2" sx={{ mb: 2 }}>
                          {comment.comment}
                        </Typography>
                      )}

                      {comment.photos && comment.photos.length > 0 && (
                        <ImageList cols={3} gap={8}>
                          {comment.photos.map((photo, idx) => (
                            <ImageListItem 
                              key={idx}
                              sx={{ cursor: 'pointer' }}
                              onClick={() => setFullscreenImage(photo)}
                            >
                              <img
                                src={photo}
                                alt={`Foto ${idx + 1}`}
                                loading="lazy"
                                style={{ objectFit: 'cover', height: 150 }}
                              />
                            </ImageListItem>
                          ))}
                        </ImageList>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No hay comentarios para esta fecha
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Diálogo para agregar comentario */}
      <Dialog 
        open={addCommentOpen} 
        onClose={() => {
          setAddCommentOpen(false);
          setNewComment('');
          setSelectedFiles([]);
          setPreviewImages([]);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Agregar Comentario - {getDayLabel(selectedDay)}
        </DialogTitle>
        <DialogContent>
          <TextField
            label="Comentario (opcional)"
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            fullWidth
            multiline
            rows={3}
            margin="normal"
          />

          <Button
            variant="outlined"
            component="label"
            startIcon={<PhotoCameraIcon />}
            sx={{ mt: 2, mb: 2 }}
          >
            Subir Fotos
            <input
              type="file"
              hidden
              multiple
              accept="image/*"
              onChange={handleFileSelect}
            />
          </Button>

          {previewImages.length > 0 && (
            <ImageList cols={3} gap={8}>
              {previewImages.map((preview, idx) => (
                <ImageListItem key={idx}>
                  <img src={preview} alt={`Preview ${idx}`} loading="lazy" />
                  <IconButton
                    sx={{
                      position: 'absolute',
                      right: 5,
                      top: 5,
                      bgcolor: 'background.paper'
                    }}
                    size="small"
                    onClick={() => handleRemoveFile(idx)}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </ImageListItem>
              ))}
            </ImageList>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setAddCommentOpen(false);
            setNewComment('');
            setSelectedFiles([]);
            setPreviewImages([]);
          }}>
            Cancelar
          </Button>
          <Button onClick={handleAddComment} variant="contained">
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo para ver imagen en fullscreen */}
      <Dialog
        open={!!fullscreenImage}
        onClose={() => setFullscreenImage(null)}
        maxWidth="lg"
        fullWidth
      >
        <DialogContent sx={{ p: 0, position: 'relative' }}>
          <IconButton
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              bgcolor: 'background.paper',
              zIndex: 1
            }}
            onClick={() => setFullscreenImage(null)}
          >
            <CloseIcon />
          </IconButton>
          {fullscreenImage && (
            <img
              src={fullscreenImage}
              alt="Vista completa"
              style={{ width: '100%', height: 'auto' }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ServiceDetail;





